import { useEffect, useState } from "react";
import { api } from "../lib/api";

/**
 * Custom application areas defined by the shop in Settings → Custom Areas.
 * Shape: { [jobType]: { wrap: [str], ppf: [str] }, ... }
 *
 * Cached module-globally so N InstallationBlocks in a single job form all
 * share one network round-trip. Refetches on `refresh()` after Settings
 * saves so the next render picks up new areas without a page reload.
 */
let cache = null;
const subs = new Set();

async function loadOnce() {
  try {
    const { data } = await api.get("/settings/custom-areas");
    cache = data || {};
  } catch {
    cache = {};
  }
  subs.forEach((s) => s(cache));
}

export function useCustomAreas() {
  const [state, setState] = useState(cache || {});
  useEffect(() => {
    subs.add(setState);
    if (cache === null) loadOnce();
    return () => subs.delete(setState);
  }, []);
  return state;
}

// Called from Settings after PUT so all open forms refresh without reload.
export function refreshCustomAreas() {
  cache = null;
  loadOnce();
}

/**
 * Merge default catalogue + shop-defined custom areas for a given jobType,
 * dedup case-insensitively, keeping defaults first for muscle-memory.
 */
export function mergeAreas(defaults, custom, jobType, kind) {
  const extras = (custom?.[jobType] || {})[kind] || [];
  if (!extras.length) return defaults;
  const seen = new Set(defaults.map((a) => a.toLowerCase()));
  const merged = [...defaults];
  for (const a of extras) {
    if (!seen.has(a.toLowerCase())) {
      merged.push(a);
      seen.add(a.toLowerCase());
    }
  }
  return merged;
}
